import {AfterViewInit, Component, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FilterBuilder, FilterComponent} from "@core/shared/models/filter";
import {OfferCreatorSearchService} from "@core/shared/services/offer-creator/offer-creator-search.service";
import {ArrayFilterField} from "@core/shared/models/filter/array-filter-field";
import {OfferAttribute} from "@core/shared/models/offer-attribute";
import {OfferAttributeService} from "@core/shared/services/offer-attribute.service";
import {forkJoin, Observable} from "rxjs";
import {TranslationService} from "@core/shared/services/translation.service";
import {TranslateService} from "@ngx-translate/core";
import {CustomerTypology} from "@core/shared/models/customer-typology";
import {CustomerTypologyService} from "@core/shared/services/customer-typology.service";
import {NetworkOfferCreator} from "@core/shared/models/network/network-offer-creator";
import {NetworkOfferCreatorService} from "@core/shared/services/network/network-offer-creator.service";
import {TextFilterComponent} from "@core/components/filter/text-filter/text-filter.component";
import {SelectArrayMultipleFilterComponent} from "@core/components/filter/select-array-multiple-filter/select-array-multiple-filter.component";
import {UserService} from "@core/shared/services/user.service";
import {User} from "@core/shared/models/user";
import {SelectArrayFilterComponent} from "@core/components/filter/select-array-filter/select-array-filter.component";
import {SelectFilterComponent} from "@core/components/filter/select-filter/select-filter.component";
import {TenderOfferCreatorStatus} from "@core/shared/models/tender/tender-offer-creator";
import {SelectGroupArrayMultipleFilterComponent} from "@core/components/filter/select-group-array-multiple-filter/select-group-array-multiple-filter.component";
import {SelectGroupConfiguration} from "@core/components/select/select-group/select-group.component";
import {OfferAttributeType, OfferAttributeTypeTagType} from "@core/shared/models/offer-attribute-type";
import {LOCALE_ITEMS, LocaleItem} from '@core/shared/models/translation';
import {map, tap} from "rxjs/operators";

export enum OfferCreatorFilterKey {
    Destination = 'offers.attributes.attribute.region',
    Types = 'offers.attributes.attribute.types',
    Target = 'offers.customerTypology.id',
    NetworkOfferCreator = 'networkOfferCreators.id',
    OfferType = 'offers.types',
    Name = 'name',
    SpokenLanguages = 'information.spokenLanguages',
    InCatalog = 'offers.inCatalog',
    HasRseLabel = 'information.hasRseLabel',
    HasSubscription = 'subscription',
    NotSelected = 'notSelected',
    ResponseType = 'responseType'
}

@Component({
    selector: 'app-core-offer-creator-filter',
    templateUrl: './offer-creator-filter.component.html',
    styleUrls: ['./offer-creator-filter.component.scss'],
    standalone: false
})
export class OfferCreatorFilterComponent implements OnInit, AfterViewInit {

    @ViewChildren(`
        destinationFilter,
        targetFilter,
        typesFilter,
        networkOfferCreatorFilter,
        offerTypeFilter,
        stayTypeFilter,
        nameFilter,
        spokenLanguagesFilter,
        inCatalogFilter,
        hasRseLabelFilter,
        hasSubscriptionFilter,
        notSelectedFilter,
        responseTypeFilter
    `) filterComponents: QueryList<FilterComponent>;

    @ViewChild('destinationFilter', {static: false}) destinationFilterComponent: SelectGroupArrayMultipleFilterComponent<OfferAttribute>;

    @ViewChild('targetFilter', {static: false}) targetFilterComponent: SelectArrayMultipleFilterComponent;

    @ViewChild('typesFilter', { static: false }) typesFilterComponent: SelectArrayMultipleFilterComponent;

    @ViewChild('networkOfferCreatorFilter', {static: false}) networkOfferCreatorFilterComponent: SelectArrayMultipleFilterComponent;

    @ViewChild('offerTypeFilter', {static: false}) offerTypeFilterComponent: SelectArrayMultipleFilterComponent;

    @ViewChild('nameFilter', { static: false }) nameFilterComponent: TextFilterComponent;

    @ViewChild('spokenLanguagesFilter', { static: false }) spokenLanguagesFilterComponent: SelectArrayMultipleFilterComponent;

    @ViewChild('inCatalogFilter', { static: false }) inCatalogFilterComponent: TextFilterComponent;

    @ViewChild('hasRseLabelFilter', { static: false }) hasRseLabelFilterComponent: TextFilterComponent;

    @ViewChild('hasSubscriptionFilter', {static: false}) hasSubscriptionFilterComponent: SelectArrayFilterComponent;

    @ViewChild('notSelectedFilter', { static: false }) notSelectedFilterComponent: TextFilterComponent;

    @ViewChild('responseTypeFilter', { static: false }) responseTypeFilterComponent: SelectFilterComponent;

    public readonly OfferCreatorFilterKey = OfferCreatorFilterKey;

    public currentUser: User = null;

    public offerTypes: { id: ('day'|'stay'), label: string }[] = [];

    public locales: LocaleItem[] = [];

    public offerAttributeTypes: { id: number, label: string }[] = [];

    public subscriptionStatus: { value: number, label: string }[] = [];

    public offerCustomerTypologies: { id: number, label: string }[] = [];

    public offerNetworkOfferCreators: { id: number, label: string }[] = [];

    public responseTypes: { id: TenderOfferCreatorStatus, label: string }[] = [];

    public regionSelectConfiguration: Partial<SelectGroupConfiguration<OfferAttribute>>;

    public regionSelectSourceCallback: () => Observable<OfferAttribute[]>;

    constructor(
        private _translateService: TranslateService,
        private _translationService: TranslationService,
        private _offerAttributeService: OfferAttributeService,
        private _customerTypologyService: CustomerTypologyService,
        private _networkOfferCreatorService: NetworkOfferCreatorService,
        public offerCreatorSearchService: OfferCreatorSearchService,
        public userService: UserService
    ) {}

    ngOnInit() {

        this.currentUser = this.userService.currentUser.value;

        this._initRegionSelect();

        this._initOfferTypes();

        this._initTypes();

        this._initLocales();

        this._initHasSubscriptions();

        this._initOfferCustomerTypologies();

        this._initOfferNetworkOfferCreators();

        this._initResponseTypes();
    }

    ngAfterViewInit(): void {

        setTimeout((): void => {

            this.initFilters();
        });
    }

    private _initRegionSelect(): void {

        this.regionSelectConfiguration = {
            label: this._translateService.instant('destination.plural.value'),
            optionLabelCallback: (option: OfferAttribute): string => {

                return `${this._translationService.getFallbackTranslation(option.translations).label}`;
            },
            compareOptionsCallback: (a: OfferAttribute, b: OfferAttribute): boolean => {

                if(!a || !b){

                    return false;
                }

                return a.id === b.id;
            },
            retrieveChildOptionsFromParentCallback: (option: OfferAttribute): OfferAttribute[] => {

                return option.children;
            },
            fieldValueCallback: (option: OfferAttribute): Partial<OfferAttribute> => {

                return {
                    id: option.id,
                    type: {
                        tag: option.type.tag
                    } as OfferAttributeType
                };
            },
            identifyOptionCallback: (option: OfferAttribute): number => {

                return option.id;
            },
            allOptionsSelectionEnabledCallback: (): boolean => {

                return false;
            },
            openChildOptionsOnParentSelectionCallback: (): boolean => {

                return true;
            }
        };

        this.regionSelectSourceCallback = (): Observable<OfferAttribute[]> => {

            const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', 'region');

            return this._offerAttributeService.getItemsAPI([typeFilter.serialize]).pipe(
                tap((): void => {

                    this.offerCreatorSearchService.filterChoicesLoaded$.next();
                })
            );
        };
    }

    private _initOfferTypes(): void {

        this.offerTypes = [
            {
                id: 'day',
                label: this._translateService.instant(`offer.type.day.value`)
            },
            {
                id: 'stay',
                label: this._translateService.instant(`offer.type.stay.value`)
            }
        ]
    }

    private _initTypes(): void {

        const observables: Observable<{ tag: OfferAttributeTypeTagType, attributes: OfferAttribute[] }>[] = [];

        const assignations: { tag: OfferAttributeTypeTagType, callback: (items: OfferAttribute[]) => void }[] = [
            {
                tag: 'types',
                callback: (items: OfferAttribute[]): void => {

                    this.offerAttributeTypes = this._mapOfferAttributes(items);
                }
            }
        ];

        assignations.forEach((assignation: { tag: OfferAttributeTypeTagType }): void => {

            const typeFilter: ArrayFilterField = new ArrayFilterField('type.tag', 'andin', assignation.tag);

            const params: string[] = [
                typeFilter.serialize
            ];

            observables.push(this._offerAttributeService.getItemsAPI(params).pipe(
                map((attributes: OfferAttribute[]): { tag: OfferAttributeTypeTagType, attributes: OfferAttribute[] } => {

                    return {
                        tag: assignation.tag,
                        attributes: attributes
                    };
                }))
            );
        });

        forkJoin(observables).subscribe((items: { tag: OfferAttributeTypeTagType, attributes: OfferAttribute[] }[]): void => {

            items.forEach((item: { tag: OfferAttributeTypeTagType, attributes: OfferAttribute[] }): void => {

                const assignation: { tag: OfferAttributeTypeTagType, callback: (items: OfferAttribute[]) => void } = assignations.find((assignation: { tag: OfferAttributeTypeTagType}): boolean => {

                    return assignation.tag === item.tag;
                });

                assignation.callback(item.attributes);
            });

            this.offerCreatorSearchService.filterChoicesLoaded$.next();
        });
    }

    private _initLocales(): void {

        this.locales = LOCALE_ITEMS.map((item: LocaleItem): LocaleItem => {

            return {
                id: item.id,
                label: this._translateService.instant(item.label)
            };
        });

        this.locales.push({
            id: 'other',
            label: this._translateService.instant('other.value')
        });
    }

    private _initHasSubscriptions(): void {

        this.subscriptionStatus = [
            {
                value: 1,
                label: this._translateService.instant(`society.subscriptions.withSubscription.value`),
            },
            {
                value: 0,
                label: this._translateService.instant(`society.subscriptions.withoutSubscription.value`)
            }
        ]
    }

    private _initOfferCustomerTypologies(): void {

        this._customerTypologyService.getItemsAPI().subscribe((customerTypologies: CustomerTypology[]): void => {

            this.offerCustomerTypologies = customerTypologies.map((item: CustomerTypology): { id: number, label: string } => {

                return {
                    id: item.id,
                    label: this._translationService.getFallbackTranslation(item.translations).name
                };
            });

            this.offerCreatorSearchService.filterChoicesLoaded$.next();
        });
    }

    private _initOfferNetworkOfferCreators(): void {

        this._networkOfferCreatorService.getItemsAPI().subscribe((networkOfferCreators: NetworkOfferCreator[]): void => {

            this.offerNetworkOfferCreators = networkOfferCreators.map((item: NetworkOfferCreator): { id: number, label: string } => {

                return {
                    id: item.id,
                    label: item.name
                };
            });

            this.offerCreatorSearchService.filterChoicesLoaded$.next();
        });
    }

    private _initResponseTypes(): void {

        this.responseTypes = [
            {
                id: 'reply',
                label: 'response.type.reply.value'
            },
            {
                id: 'no-reply',
                label: 'response.type.no-reply.value'
            },
            {
                id: 'to-process',
                label: 'response.type.to-process.value'
            }
        ];
    }

    private _mapOfferAttributes(items: OfferAttribute[]): { id: number, label: string }[] {

        return items.map((item: OfferAttribute): { id: number, label: string } => {

            return {
                id: item.id,
                label: this._translationService.getFallbackTranslation(item.translations).label
            };

        }).sort((a: { label: string }, b: { label: string }): number => {

            return a.label.localeCompare(b.label);
        });
    }

    public initFilters(): void {

        if (!this.offerCreatorSearchService.sessionEnabled || !this.offerCreatorSearchService.hasSessionFilters) {

            return;
        }

        // Destinations

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.Destination) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.Destination)){

            this.destinationFilterComponent.values = (this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.Destination).value as OfferAttribute[]);
        }

        // Cibles

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.Target) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.Target)){

            this.targetFilterComponent.values = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.Target).value;
        }

        // Types de séjours

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.Types) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.Types)){

            this.typesFilterComponent.values = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.Types).value;
        }

        // Réseaux de créateurs d'offres

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.NetworkOfferCreator) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.NetworkOfferCreator)){

            this.networkOfferCreatorFilterComponent.values = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.NetworkOfferCreator).value;
        }

        // Type d'offres

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.OfferType) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.OfferType)){

            this.offerTypeFilterComponent.values = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.OfferType).value;
        }

        // Avec / sans abonnement

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.HasSubscription) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.HasSubscription)){

            this.hasSubscriptionFilterComponent.value = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.HasSubscription).value;
        }

        // Nom

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.Name) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.Name)){

            this.nameFilterComponent.value = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.Name).value;
        }

        // Langues parlées

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.SpokenLanguages) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.SpokenLanguages)){

            this.spokenLanguagesFilterComponent.values = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.SpokenLanguages).value;
        }

        // Présent dans mon catalogue

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.InCatalog) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.InCatalog)){

            this.inCatalogFilterComponent.value = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.InCatalog).value;
        }

        // Labellisé RSE

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.HasRseLabel) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.HasRseLabel)){

            this.hasRseLabelFilterComponent.value = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.HasRseLabel).value;
        }

        // Créateurs pas encore sélectionnés

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.NotSelected) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.NotSelected)){

            this.notSelectedFilterComponent.value = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.NotSelected).value;
        }

        // Type de réponse

        if(this.offerCreatorSearchService.isFilterEnabled(OfferCreatorFilterKey.ResponseType) && this.offerCreatorSearchService.hasSessionFilter(OfferCreatorFilterKey.ResponseType)){

            this.responseTypeFilterComponent.value = this.offerCreatorSearchService.getSessionFilter(OfferCreatorFilterKey.ResponseType).value;
        }
    }

    public reset(): void {

        this.filterComponents.forEach((filterComponent: FilterComponent): void => {

            filterComponent.reset();
        });

        this.offerCreatorSearchService.resetFilter$.next();
    }

    get filterBuilder(): FilterBuilder {

        return this.offerCreatorSearchService.filterBuilder;
    }
}
